import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { Save } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { inputClass, labelClass, sectionClass, primaryBtnClass } from "@/lib/styleClasses"

interface UbyportIdentFormValues {
    ubyportIdub: string
    ubyportMark: string
}

interface UbyportCredsFormValues {
    ntlmDomain: string
    ntlmUser: string
    ntlmPassword: string
}

interface UbyportConfigStatus {
    hasCredentials: boolean
    ntlmDomain: string | null
    ntlmUser: string | null
    enabled: boolean
    env: "test" | "prod"
    testedAt: string | null
    idub: string | null
    mark: string | null
    isOwner: boolean
}

export const UbyportSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)

    const configQuery = useQuery({
        queryKey: ["admin", "ubyport-config", areal.id],
        queryFn: () => api.get<UbyportConfigStatus>(`/areals/${areal.id}/ubyport-config`),
    })
    const config = configQuery.data

    const identForm = useForm<UbyportIdentFormValues>({
        values: {
            ubyportIdub: areal.ubyportIdub ?? "",
            ubyportMark: areal.ubyportMark ?? "",
        },
    })

    const credsForm = useForm<UbyportCredsFormValues>({
        values: {
            ntlmDomain: config?.ntlmDomain ?? "",
            ntlmUser: config?.ntlmUser ?? "",
            ntlmPassword: "",
        },
    })

    const saveIdentMutation = useMutation({
        mutationFn: (values: UbyportIdentFormValues) =>
            api.patch<ArealResponse>(`/areals/${areal.id}`, {
                ubyportIdub: values.ubyportIdub.trim() || null,
                ubyportMark: values.ubyportMark.trim() || null,
            }),
        onSuccess: () => {
            toast.success(t("admin.settings.toastSaved"))
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
            queryClient.invalidateQueries({ queryKey: ["admin", "ubyport-config", areal.id] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    const saveCredsMutation = useMutation({
        mutationFn: (values: UbyportCredsFormValues) =>
            api.patch<{ saved: boolean }>(`/areals/${areal.id}/ubyport-config`, {
                ntlmDomain: values.ntlmDomain.trim(),
                ntlmUser: values.ntlmUser.trim(),
                ntlmPassword: values.ntlmPassword,
            }),
        onSuccess: () => {
            toast.success(t("admin.settings.ubyport.credsSaved"))
            credsForm.reset({ ntlmDomain: credsForm.getValues("ntlmDomain"), ntlmUser: credsForm.getValues("ntlmUser"), ntlmPassword: "" })
            setTestResult(null)
            queryClient.invalidateQueries({ queryKey: ["admin", "ubyport-config", areal.id] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    const deleteCredsMutation = useMutation({
        mutationFn: () => api.delete(`/areals/${areal.id}/ubyport-config`),
        onSuccess: () => {
            toast.success(t("admin.settings.ubyport.credsDeleted"))
            setTestResult(null)
            queryClient.invalidateQueries({ queryKey: ["admin", "ubyport-config", areal.id] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    const toggleEnabledMutation = useMutation({
        mutationFn: (enabled: boolean) =>
            api.patch<{ enabled: boolean }>(`/areals/${areal.id}/ubyport-config/enabled`, { enabled }),
        onSuccess: (res) => {
            toast.success(res.enabled ? t("admin.settings.ubyport.enabledOn") : t("admin.settings.ubyport.enabledOff"))
            queryClient.invalidateQueries({ queryKey: ["admin", "ubyport-config", areal.id] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    const setEnvMutation = useMutation({
        mutationFn: (env: "test" | "prod") =>
            api.patch<{ env: "test" | "prod"; reset: boolean }>(`/areals/${areal.id}/ubyport-config/env`, { env }),
        onSuccess: (res) => {
            toast.success(t("admin.settings.ubyport.envSwitched", {
                env: t(`admin.settings.ubyport.env${res.env === "prod" ? "Prod" : "Test"}`),
            }))
            setTestResult(null)
            queryClient.invalidateQueries({ queryKey: ["admin", "ubyport-config", areal.id] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    const testMutation = useMutation({
        mutationFn: () => api.post<{ available: boolean; durationMs: number; env: string }>(`/areals/${areal.id}/ubyport/test`, {}),
        onSuccess: (res) => {
            setTestResult({
                ok: res.available,
                message: res.available
                    ? t("admin.settings.ubyport.testOk", { env: res.env, ms: res.durationMs })
                    : t("admin.settings.ubyport.testUnavailable"),
            })
            queryClient.invalidateQueries({ queryKey: ["admin", "ubyport-config", areal.id] })
        },
        onError: (err) => {
            const msg = err instanceof ApiRequestError ? err.message : String(err)
            setTestResult({ ok: false, message: msg })
        },
    })

    const isOwner = config?.isOwner ?? false
    const canEnable = isOwner && !!config?.hasCredentials && !!config?.testedAt && !!config?.idub && !!config?.mark
    const testedAtLabel = config?.testedAt
        ? new Date(config.testedAt).toLocaleString()
        : t("admin.settings.ubyport.neverTested")

    return (
        <div className={sectionClass}>
            <h2 className="text-lg font-black text-slate-900">{t("admin.settings.ubyport.title")}</h2>
            <p className="text-sm font-bold text-slate-500">{t("admin.settings.ubyport.intro")}</p>

            {!isOwner && configQuery.isFetched && (
                <p className="text-sm font-bold text-slate-400 italic">
                    {t("admin.settings.ubyport.managerReadOnly")}
                </p>
            )}

            {/* Environment switch — which Foreign Police endpoint this camp
                reports to. Switching resets the test/enabled state server-side. */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <label className={labelClass}>{t("admin.settings.ubyport.envLabel")}</label>
                    <div className="inline-flex rounded-2xl bg-white border border-slate-200 p-1">
                        {(["test", "prod"] as const).map((envOpt) => (
                            <button
                                key={envOpt}
                                type="button"
                                disabled={!isOwner || setEnvMutation.isPending || config?.env === envOpt}
                                onClick={() => {
                                    if (envOpt === "prod" && !window.confirm(t("admin.settings.ubyport.envProdConfirm"))) return
                                    setEnvMutation.mutate(envOpt)
                                }}
                                className={cn(
                                    "px-4 py-1.5 rounded-xl text-sm font-black transition-colors disabled:cursor-default",
                                    config?.env === envOpt
                                        ? envOpt === "prod"
                                            ? "bg-red-600 text-white"
                                            : "bg-slate-700 text-white"
                                        : "text-slate-500 hover:text-slate-700",
                                )}
                            >
                                {t(`admin.settings.ubyport.env${envOpt === "prod" ? "Prod" : "Test"}`)}
                            </button>
                        ))}
                    </div>
                </div>
                {config?.env === "prod" ? (
                    <p className="text-xs font-black text-red-600">{t("admin.settings.ubyport.envProdWarning")}</p>
                ) : (
                    <p className="text-xs font-bold text-slate-500">{t("admin.settings.ubyport.envTestHint")}</p>
                )}
            </div>

            {/* IDUB + Mark — owner only */}
            <form onSubmit={identForm.handleSubmit((v) => saveIdentMutation.mutate(v))} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>{t("admin.settings.ubyport.idub")}</label>
                        <input {...identForm.register("ubyportIdub")} className={inputClass} placeholder="2396201258877" />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.ubyport.mark")}</label>
                        <input {...identForm.register("ubyportMark")} className={inputClass} placeholder="TWS239" />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button type="submit" disabled={!isOwner || saveIdentMutation.isPending || !identForm.formState.isDirty} className={primaryBtnClass}>
                        <Save className="size-3.5" />
                        {saveIdentMutation.isPending ? t("admin.settings.saving") : t("admin.settings.save")}
                    </button>
                </div>
            </form>

            {/* NTLM credentials */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h3 className="text-sm font-black text-slate-900">{t("admin.settings.ubyport.credsTitle")}</h3>
                    <div className="flex items-center gap-2 text-xs font-bold">
                        {config?.hasCredentials ? (
                            <span className="px-2 py-1 rounded-xl bg-green-100 text-green-700">{t("admin.settings.ubyport.credsStored")}</span>
                        ) : (
                            <span className="px-2 py-1 rounded-xl bg-slate-100 text-slate-600">{t("admin.settings.ubyport.credsMissing")}</span>
                        )}
                    </div>
                </div>
                <p className="text-xs font-bold text-slate-500">{t("admin.settings.ubyport.credsIntro")}</p>

                <form onSubmit={credsForm.handleSubmit((v) => isOwner && saveCredsMutation.mutate(v))} className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className={labelClass}>{t("admin.settings.ubyport.ntlmDomain")}</label>
                            <input {...credsForm.register("ntlmDomain", { required: true })} className={inputClass} placeholder="EXRESORTMV" />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.settings.ubyport.ntlmUser")}</label>
                            <input {...credsForm.register("ntlmUser", { required: true })} className={inputClass} placeholder="Uby-WS_..." />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.settings.ubyport.ntlmPassword")}</label>
                            <input
                                type="password"
                                autoComplete="new-password"
                                {...credsForm.register("ntlmPassword", { required: true })}
                                className={inputClass}
                                placeholder={config?.hasCredentials ? t("admin.settings.ubyport.passwordReplace") : ""}
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        {isOwner && config?.hasCredentials && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (window.confirm(t("admin.settings.ubyport.credsDeleteConfirm"))) {
                                        deleteCredsMutation.mutate()
                                    }
                                }}
                                disabled={deleteCredsMutation.isPending}
                                className="px-4 py-2 rounded-2xl bg-red-50 text-red-600 font-bold text-sm hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                                {t("admin.settings.ubyport.credsDelete")}
                            </button>
                        )}
                        <button type="submit" disabled={!isOwner || saveCredsMutation.isPending} className={primaryBtnClass}>
                            <Save className="size-3.5" />
                            {saveCredsMutation.isPending ? t("admin.settings.saving") : t("admin.settings.ubyport.credsSave")}
                        </button>
                    </div>
                </form>
            </div>

            {/* Test + enabled toggle */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs font-bold text-slate-500">
                        {t("admin.settings.ubyport.lastTested")}: <span className="text-slate-700">{testedAtLabel}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => testMutation.mutate()}
                        disabled={!isOwner || testMutation.isPending || !config?.hasCredentials}
                        className="px-5 py-2 rounded-2xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200 transition-colors disabled:opacity-50"
                    >
                        {testMutation.isPending
                            ? t("admin.settings.ubyport.testing")
                            : t("admin.settings.ubyport.testAvailability")}
                    </button>
                </div>

                <label className={cn(
                    "flex items-center gap-3 p-3 rounded-2xl border",
                    canEnable ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 opacity-60",
                )}>
                    <input
                        type="checkbox"
                        checked={!!config?.enabled}
                        disabled={!canEnable || toggleEnabledMutation.isPending}
                        onChange={(e) => toggleEnabledMutation.mutate(e.target.checked)}
                        className="size-5 accent-brand"
                    />
                    <div className="flex-1">
                        <div className="text-sm font-black text-slate-900">{t("admin.settings.ubyport.enableLabel")}</div>
                        <div className="text-xs font-bold text-slate-500">{t("admin.settings.ubyport.enableHint")}</div>
                    </div>
                </label>
            </div>

            {testResult && (
                <div className={cn(
                    "rounded-2xl p-4 text-sm font-bold border",
                    testResult.ok ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200",
                )}>
                    {testResult.message}
                </div>
            )}
        </div>
    )
}
